import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, Container } from "@/components/common/Primitives";
import { PageHero, SectionHead, Rows, Kicker, Headline, Lead, TextLink, RuleDraw, ClosingBand, Success, Field, EditorialRow, INPUT_CLS, TEXTAREA_CLS } from "@/components/common/Editorial";
import { PHONE } from "@/components/layout/Header";
import { api, errorCode } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";
import { useContent } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";

/* copy (binding – memory/copy_deck_nynorsk.md §5) */
const IMAGE_POS = { wedding: "50% 40%", memorial: "50% 50%", conference: "50% 50%", experience: "50% 50%" };

const PLAN_TYPES = [
  { id: "wedding", nn: "Bryllaup", en: "Wedding" },
  { id: "confirmation", nn: "Konfirmasjon", en: "Confirmation" },
  { id: "birthday", nn: "Bursdag eller jubileum", en: "Birthday or anniversary" },
  { id: "memorial", nn: "Minnestund", en: "Memorial gathering" },
  { id: "conference", nn: "Møte, kurs eller konferanse", en: "Meeting, course or conference" },
  { id: "other", nn: "Anna selskap", en: "Other celebration" },
];
const TYPE_ALIAS = { celebration: "wedding", experience: "other" };

const SERVING = [
  { id: "suggest", nn: "Me ønskjer forslag", en: "We would like a suggestion" },
  { id: "lunch", nn: "Lunsj", en: "Lunch" },
  { id: "dinner", nn: "Middag", en: "Dinner" },
  { id: "coffee", nn: "Kaffi og noko å bite i", en: "Coffee and something to eat" },
  { id: "several", nn: "Fleire måltid", en: "Several meals" },
];
const LODGING = [
  { id: "yes", nn: "Ja", en: "Yes" },
  { id: "no", nn: "Nei", en: "No" },
  { id: "tbd", nn: "Ikkje avklart enno", en: "Not decided yet" },
];

const CAPACITY = {
  nn: [["Restaurant", "Plass til opptil 150 gjester."], ["Møte og konferanse", "Lokale for samlingar frå 2 til 250 personar. Kapasiteten avheng av lokale og oppsett."], ["Overnatting", "89 hotellrom i ulike kategoriar."], ["Servering", "Meny og måltid etter avtale."], ["Parkering", "Gratis ved hotellet."], ["Reise", "Sjå reisealternativ med bil, båt og kollektivtransport."]],
  en: [["Restaurant", "Seats up to 150 guests."], ["Meetings and conferences", "Rooms for gatherings from 2 to 250 people. Capacity depends on the room and the layout."], ["Accommodation", "89 hotel rooms in several categories."], ["Catering", "Menu and meals by arrangement."], ["Parking", "Free at the hotel."], ["Travel", "See travel options by car, boat and public transport."]],
};

const STEPS = {
  nn: [
    ["Send oss det de veit", "Dato eller tidsrom, om lag kor mange de blir, og kva de ønskjer hjelp til. De treng ikkje ha alle detaljane klare."],
    ["Me lagar eit forslag", "Saman går me gjennom lokale, servering og eventuell overnatting. De kan òg avtale eit besøk for å sjå lokala."],
    ["Avtal detaljane", "Når innhaldet og prisen er avtalte, får de ei skriftleg stadfesting."],
  ],
  en: [
    ["Send us what you know", "A date or period, roughly how many you will be, and what you would like help with. You don't need to have every detail ready."],
    ["We draft a proposal", "Together we go through the venue, catering and any accommodation. You can also arrange a visit to see the rooms."],
    ["Agree the details", "Once the content and price are agreed, you receive written confirmation."],
  ],
};

export default function Events() {
  const [sp] = useSearchParams();
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  usePageMeta(tx("Selskap og møte", "Events and meetings"), tx("Samlast ved fjorden – bryllaup, selskap, minnestund, møte og konferanse i Rosendal. Lokale, mat og rom på same stad.", "Gather by the fjord – weddings, celebrations, memorials, meetings and conferences in Rosendal. Venue, food and rooms in one place."));
  const types = useMemo(() => [...(data?.event_types || [])].sort((a, b) => (a.sort || 99) - (b.sort || 99)), [data]);
  const initialType = (() => { const t = sp.get("type") || ""; const id = TYPE_ALIAS[t] || t; return PLAN_TYPES.some((p) => p.id === id) ? id : ""; })();
  const [form, setForm] = useState({ event_type: initialType, name: "", email: "", phone: "", company: "", date: "", guests: "", lodging: "", serving: "", message: "", website: "" });
  const [done, setDone] = useState(false);
  useEffect(() => {
    const t = sp.get("type");
    if (t) { const id = TYPE_ALIAS[t] || t; if (PLAN_TYPES.some((p) => p.id === id)) setForm((f) => ({ ...f, event_type: id })); }
  }, [sp]);
  useEffect(() => {
    if (window.location.hash === "#forespurnad") setTimeout(() => document.getElementById("forespurnad")?.scrollIntoView({ behavior: "smooth" }), 400);
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const label = (list, id) => { const it = list.find((x) => x.id === id); return it ? (lang === "en" ? it.en : it.nn) : ""; };
  const send = useMutation({
    mutationFn: async () => (await api.post("/inquiries", {
      kind: "event", ...form,
      event_type: label(PLAN_TYPES, form.event_type) || form.event_type,
      serving: label(SERVING, form.serving),
      lodging: label(LODGING, form.lodging),
      guests: form.guests ? parseInt(form.guests, 10) : null, lang,
    })).data,
    onSuccess: () => { setDone(true); toast.success(tx("Takk! Me har fått førespurnaden.", "Thank you! We have received your enquiry.")); },
    onError: (err) => toast.error(errorText(errorCode(err), lang)),
  });
  const pick = (id) => { const mapped = TYPE_ALIAS[id] || id; if (PLAN_TYPES.some((p) => p.id === mapped)) set("event_type", mapped); document.getElementById("forespurnad")?.scrollIntoView({ behavior: "smooth" }); };
  const steps = lang === "en" ? STEPS.en : STEPS.nn;

  return (
    <div data-testid="events-page">
      <PageHero
        testid="events-hero"
        image="/images/wedding.jpg"
        position="50% 42%"
        alt={tx("Dekka langbord til selskap i Rosendal", "A long table set for a celebration in Rosendal")}
        kicker={tx("Selskap og møte", "Events and meetings")}
        title={tx("Samlast ved fjorden", "Gather by the fjord")}
        lead={tx("Ei feiring med familien eller ei samling med kollegaer. Me hjelper dykk med lokale, mat og rom til dei som vil overnatte.", "A celebration with the family or a gathering with colleagues. We help you with the venue, the food and rooms for those who want to stay over.")}
      >
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
          <Button size="lg" variant="inverse" onClick={() => pick(form.event_type)} data-testid="events-hero-cta">{tx("Fortel oss om planane", "Tell us about your plans")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Button>
          <p className="hero-shadow text-sm text-white/80">{tx("Førespurnaden er uforpliktande.", "The enquiry is non-binding.")}</p>
        </div>
      </PageHero>

      {/* Event types – editorial rows */}
      <section className="pt-24 lg:pt-32" data-testid="events-types">
        <Container className="space-y-20 lg:space-y-28">
          {types.map((e, i) => (
            <Reveal key={e.id} data-testid={`events-type-${e.id}`}>
              <EditorialRow
                src={e.image}
                position={IMAGE_POS[e.id] || "50% 50%"}
                index={i + 1}
                label={tr(e.title)}
                title={tr(e.heading) || tr(e.title)}
                body={tr(e.text)}
                flip={i % 2 === 1}
                eager={i === 0}
              >
                <div className="mt-7">
                  <Button variant="outline" onClick={() => pick(e.id)} data-testid={`events-type-${e.id}-cta`}>{tr(e.cta) || tx("Send førespurnad", "Send an enquiry")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Button>
                </div>
              </EditorialRow>
            </Reveal>
          ))}
        </Container>
      </section>

      {/* Capacity */}
      <section className="pt-24 lg:pt-32" data-testid="events-facts">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <Kicker className="mb-5">{tx("Lokale og kapasitet", "Venues and capacity")}</Kicker>
              <Headline>{tx("Lokale, mat og rom på same stad", "Venue, food and rooms in one place")}</Headline>
              <div className="mt-8"><TextLink to="/praktisk#veg" testid="events-directions-link">{tx("Slik kjem de til hotellet", "How to get to the hotel")}</TextLink></div>
            </Reveal>
            <Reveal delay={0.05} className="lg:col-span-7 lg:pt-2">
              <Rows rows={lang === "en" ? CAPACITY.en : CAPACITY.nn} testid="events-facts-list" labelWidth="sm:grid-cols-[13rem_1fr]" size="lg" />
            </Reveal>
          </div>
        </Container>
      </section>

      {/* Steps */}
      <section className="pt-24 lg:pt-32" data-testid="events-steps-section">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <Reveal><SectionHead kicker={tx("Planlegging", "Planning")} title={tx("Slik planlegg me saman", "How we plan together")} /></Reveal>
          <ol className="mt-12 grid gap-x-10 gap-y-10 lg:mt-16 lg:grid-cols-3" data-testid="events-steps">
            {steps.map(([t, body], i) => (
              <Reveal as="li" key={t} delay={i * 0.06} className="border-t border-foreground pt-6">
                <p className="index-num text-muted-foreground tnum">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-4 text-[1.375rem] font-medium leading-snug tracking-[-0.015em] text-foreground">{t}</p>
                <p className="mt-3 max-w-[40ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{body}</p>
              </Reveal>
            ))}
          </ol>
        </Container>
      </section>

      {/* Enquiry */}
      <section id="forespurnad" className="scroll-mt-24 py-24 lg:py-32" data-testid="events-inquiry">
        <Container>
          <RuleDraw className="mb-16 lg:mb-24" />
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <Reveal className="lg:sticky lg:top-28">
                <Kicker className="mb-5">{tx("Førespurnad", "Enquiry")}</Kicker>
                <Headline>{tx("Fortel oss om planane", "Tell us about your plans")}</Headline>
                <Lead>{tx("Skriv det de veit om dato, gjester og kva de ønskjer. Me tek kontakt for å gå gjennom resten.", "Write what you know about the date, guests and what you would like. We will get in touch to go through the rest.")}</Lead>
                <div className="mt-8 border-t border-border pt-6 text-[0.9375rem] text-muted-foreground">
                  <p>{tx("Vil de heller ringje?", "Would you rather call?")} <a href={PHONE.tel} className="font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground tabular">{PHONE.display}</a></p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              {done ? (
                <Success testid="events-inquiry-success" title={tx("Takk! Me har fått førespurnaden og tek kontakt for å gå gjennom planane med dykk.", "Thank you! We have received your enquiry and will be in touch to go through your plans.")} body={tx("Innsendinga er ein førespurnad. Arrangementet er først bestilt når det er stadfesta av hotellet.", "This is an enquiry. The event is only booked once it has been confirmed by the hotel.")} />
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); send.mutate(); }} className="grid gap-6 sm:grid-cols-2" data-testid="events-inquiry-form">
                  <Field id="inquiry-event-type" label={tx("Kva planlegg de?", "What are you planning?")} className="sm:col-span-2">
                    <Select value={form.event_type || "unset"} onValueChange={(v) => set("event_type", v === "unset" ? "" : v)}>
                      <SelectTrigger id="inquiry-event-type" className={INPUT_CLS} data-testid="inquiry-event-type"><SelectValue placeholder={tx("Vel arrangementstype", "Choose the type of event")} /></SelectTrigger>
                      <SelectContent className="rounded-lg">
                        <SelectItem value="unset">{tx("Vel arrangementstype", "Choose the type of event")}</SelectItem>
                        {PLAN_TYPES.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`inquiry-event-type-${t.id}`}>{lang === "en" ? t.en : t.nn}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field id="inq-name" label={tx("Namn", "Name")}><Input id="inq-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={INPUT_CLS} autoComplete="name" data-testid="inquiry-name" /></Field>
                  <Field id="inq-company" label={tx("Firma eller organisasjon (valfritt)", "Company or organisation (optional)")}><Input id="inq-company" value={form.company} onChange={(e) => set("company", e.target.value)} className={INPUT_CLS} autoComplete="organization" data-testid="inquiry-company" /></Field>
                  <Field id="inq-email" label={tx("E-postadresse", "Email address")}><Input id="inq-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className={INPUT_CLS} autoComplete="email" data-testid="inquiry-email" /></Field>
                  <Field id="inq-phone" label={tx("Telefon", "Phone")}><Input id="inq-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={INPUT_CLS} autoComplete="tel" data-testid="inquiry-phone" /></Field>
                  <Field id="inq-date" label={tx("Ønskt dato eller tidsrom", "Preferred date or period")} hint={tx("Skriv gjerne fleire alternativ dersom datoen ikkje er fastsett.", "Feel free to give several options if the date is not set.")}><Input id="inq-date" value={form.date} onChange={(e) => set("date", e.target.value.slice(0, 80))} className={INPUT_CLS} placeholder={tx("T.d. 14. juni 2027, eller helga 12.–14. juni", "E.g. 14 June 2027, or the weekend of 12–14 June")} data-testid="inquiry-date" /></Field>
                  <Field id="inq-guests" label={tx("Om lag kor mange blir de?", "Roughly how many will you be?")}><Input id="inq-guests" type="number" min="1" inputMode="numeric" value={form.guests} onChange={(e) => set("guests", e.target.value)} className={INPUT_CLS} data-testid="inquiry-guests" /></Field>
                  <Field id="inquiry-lodging" label={tx("Ønskjer de overnatting?", "Would you like accommodation?")}>
                    <Select value={form.lodging || "unset"} onValueChange={(v) => set("lodging", v === "unset" ? "" : v)}>
                      <SelectTrigger id="inquiry-lodging" className={INPUT_CLS} data-testid="inquiry-lodging"><SelectValue placeholder={tx("Vel", "Choose")} /></SelectTrigger>
                      <SelectContent className="rounded-lg">
                        <SelectItem value="unset">{tx("Vel", "Choose")}</SelectItem>
                        {LODGING.map((t) => <SelectItem key={t.id} value={t.id}>{lang === "en" ? t.en : t.nn}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field id="inquiry-serving" label={tx("Kva ønskjer de av servering?", "What would you like served?")}>
                    <Select value={form.serving || "suggest"} onValueChange={(v) => set("serving", v)}>
                      <SelectTrigger id="inquiry-serving" className={INPUT_CLS} data-testid="inquiry-serving"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-lg">
                        {SERVING.map((t) => <SelectItem key={t.id} value={t.id}>{lang === "en" ? t.en : t.nn}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field id="inq-message" label={tx("Kva meir bør me vite?", "What else should we know?")} className="sm:col-span-2">
                    <Textarea id="inq-message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value.slice(0, 1400))} className={TEXTAREA_CLS} placeholder={tx("Fortel gjerne om program, lokale, budsjett eller andre omsyn.", "Tell us about the programme, venue, budget or anything else to consider.")} data-testid="inquiry-message" />
                  </Field>
                  <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  <div className="flex flex-col gap-5 border-t border-border pt-6 sm:col-span-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-md text-xs leading-relaxed text-muted-foreground">{tx("Me brukar opplysningane til å følgje opp førespurnaden.", "We use your details to follow up on the enquiry.")} <Link to="/praktisk#vilkar" className="underline decoration-border underline-offset-4 hover:decoration-foreground">{tx("Les personvernerklæringa", "Read the privacy policy")}</Link></p>
                    <Button type="submit" size="lg" disabled={send.isPending} data-testid="inquiry-submit">{send.isPending ? tx("Sender …", "Sending …") : tx("Send førespurnad", "Send enquiry")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </Container>
      </section>

      <ClosingBand
        testid="events-closing"
        title={tx("Når vil du kome?", "When would you like to come?")}
        lead={tx("Vel datoane dine og sjå ledige rom. Frukost er inkludert i alle hotellrom.", "Choose your dates and see available rooms. Breakfast is included in all hotel rooms.")}
        image="/images/hall-banquet.jpg"
        position="50% 50%"
      >
        <Button asChild size="lg" variant="inverse" data-testid="events-closing-cta"><Link to="/bestill">{tx("Sjå ledige rom", "See available rooms")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} /></Link></Button>
        <Button asChild size="lg" variant="outline-light" className="tabular" data-testid="events-closing-phone"><a href={PHONE.tel}><Phone className="h-4 w-4" strokeWidth={1.5} /> {tx("Ring", "Call")} {PHONE.display}</a></Button>
      </ClosingBand>
    </div>
  );
}
